import { Title } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { DataGrid, Performance } from "@/components/elements";
import { LabelContentLocker } from "@/components/widgets/ContentLocker/LabelContentLocker";
import { getLimitedLabels } from "@/config/plans.config";
import { defaultCurrency } from "@/lib/currency";
import { formatCurrency, formatNumber } from "@/utils/formatEuro";

import type { LabelValueProps } from "@/components/elements";
import type { Currency } from "@/types/currency";
import type { Garage } from "@/types/garage";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type GarageMetricsViewProps = {
  data?: Garage["data"];
  loading?: boolean;
} & MantineStyleProps;

export const GarageMetricsView: FC<GarageMetricsViewProps> = ({ data, ...props }) => {
  const { t } = useTranslation();
  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const values: LabelValueProps[] = data
    ? ([
        {
          label: t("garage.purchase_price"),
          value:
            data.acquisition_cost !== undefined &&
            formatCurrency(data.acquisition_cost, currency.code),
        },
        /* {
          label: t("garage.roi"),
          value: data.roi !== undefined ? formatNumber(data.roi, { style: "percent" }) : "-",
          infoIcon: t("tooltips.roi"),
        }, */
        {
          label: t("garage.max_drawdown"),
          value:
            data.max_drawdown !== undefined &&
            !getLimitedLabels.includes(t("garage.max_drawdown")) ? (
              formatNumber(data.max_drawdown, { style: "percent", maximumFractionDigits: 1 })
            ) : (
              <LabelContentLocker />
            ),
          infoIcon: t("tooltips.maxDrawdown"),
        },
        {
          label: t("garage.time_to_recovery"),
          value:
            data.time_to_recovery &&
            t("garage.time_to_recovery_value", { count: data.time_to_recovery }),
          infoIcon: t("tooltips.timeToRecovery"),
        },
        {
          label: t("garage.sharpe_ratio"),
          value:
            data.sharpe_ratio !== undefined &&
            getLimitedLabels.includes(t("garage.sharpe_ratio")) ? (
              formatNumber(data.sharpe_ratio, { maximumFractionDigits: 3 })
            ) : (
              <LabelContentLocker />
            ),
          infoIcon: t("tooltips.sharpeRatio"),
        },
        data.best_performing_car?.name
          ? {
              label: t("garage.best_performing_car_1y"),
              value: (
                <Performance
                  description={data.best_performing_car.name}
                  value={data.best_performing_car.value}
                />
              ),
            }
          : undefined,
        data.worst_performing_car?.name
          ? {
              label: t("garage.worst_performing_car_1y"),
              value: (
                <Performance
                  description={data.worst_performing_car.name}
                  value={data.worst_performing_car.value}
                />
              ),
            }
          : undefined,
      ].filter(Boolean) as LabelValueProps[])
    : [];

  return (
    <DataGrid
      cols={{ base: 2, sm: 4 }}
      items={values}
      title={<Title order={3}>{t("garage.metrics")}</Title>}
      {...props}
    />
  );
};
